import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import WalkKitBuilder from "../components/WalkKitBuilder";
import { api, PRODUCTS, BUNDLE, money } from "../lib/data";

const CATEGORIES = ["All", "Walk", "Travel", "Home", "Groom"];

export default function Shop() {
  const [products, setProducts] = useState(PRODUCTS);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-28 md:pt-36" data-testid="shop-page">
      <header className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-rust">The shop</p>
          <h1 className="mt-4 max-w-3xl font-fraunces text-5xl font-light leading-[1.02] md:text-7xl">
            Everything for the miles ahead.
          </h1>
          <p className="mt-6 max-w-xl font-manrope text-lg text-ink">
            Three things to start. More coming — for the walk, the road, and everything between.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3" data-testid="shop-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`category-${c.toLowerCase()}`}
              className={`rounded-full border px-5 py-2 font-manrope text-sm transition-colors ${
                cat === c ? "border-charcoal bg-charcoal text-cream" : "border-charcoal/20 text-charcoal hover:border-charcoal"
              }`}
            >
              {c}
              {c !== "All" && <span className="ml-2 text-xs opacity-50">soon</span>}
            </button>
          ))}
        </div>
      </header>

      <section className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-20">
        {cat !== "All" ? (
          <div className="flex min-h-[30vh] flex-col items-start justify-center border-t border-charcoal/10 pt-10" data-testid="category-empty">
            <h2 className="font-fraunces text-3xl md:text-4xl">{cat} is on its way.</h2>
            <p className="mt-3 max-w-md font-manrope text-ink">
              We're building out the {cat.toLowerCase()} collection. For now, start with the essentials.
            </p>
            <button onClick={() => setCat("All")} className="group mt-6 inline-flex items-center gap-2 font-manrope font-semibold hover:text-rust">
              Back to all gear <ArrowRight className="hover-arrow h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <Reveal className="mb-14">
              <div className="flex flex-col items-start justify-between gap-6 bg-forest p-8 text-cream md:flex-row md:items-center md:p-10" data-testid="shop-bundle">
                <div>
                  <span className="font-manrope text-xs uppercase tracking-[0.15em] text-cream/70">Best value</span>
                  <h2 className="mt-2 font-fraunces text-3xl md:text-4xl">The Walk Kit — all three, together.</h2>
                  <p className="mt-2 max-w-lg font-manrope text-cream/80">{BUNDLE.short}</p>
                </div>
                <div className="flex flex-shrink-0 flex-wrap items-center gap-4">
                  <span className="font-fraunces text-2xl">{money(BUNDLE.price)}</span>
                  <span className="font-manrope text-cream/60 line-through">{money(BUNDLE.compareAt)}</span>
                  <span data-testid="shop-bundle-save" className="rounded-full bg-rust px-3 py-1 font-manrope text-xs font-bold text-cream">Save {money(BUNDLE.compareAt - BUNDLE.price)}</span>
                  <WalkKitBuilder
                    trigger={
                      <button data-testid="shop-add-bundle" className="group flex items-center gap-2 rounded-full bg-rust px-6 py-3 font-manrope font-semibold text-cream transition-colors hover:bg-cream hover:text-charcoal">
                        Build the kit <ArrowRight className="hover-arrow h-4 w-4" />
                      </button>
                    }
                  />
                </div>
              </div>
            </Reveal>
            <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/products/${p.slug}`} className="group block" data-testid={`shop-card-${p.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-white ring-1 ring-charcoal/10">
                    <img src={p.showcaseImage || p.gallery[0]} alt={p.name} className="img-zoom h-full w-full object-contain p-8" />
                  </div>
                  <p className="mt-5 font-manrope text-xs uppercase tracking-[0.15em] text-rust">{p.tagline}</p>
                  <div className="mt-2 flex items-start justify-between gap-3">
                    <h3 className="font-fraunces text-2xl leading-tight">{p.name.split("+")[0].trim()}</h3>
                    <span className="whitespace-nowrap font-manrope font-semibold">{money(p.price, p.currency)}</span>
                  </div>
                  <p className="mt-2 font-manrope text-ink">{p.short}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          </>
        )}
      </section>
    </div>
  );
}
